import IMask, { type FactoryOpts } from 'imask'

/**
 * Editable fields for medical billing summary entries
 */
export enum EditableEntryField {
  SERVICE_DATE = 'service_date',
  FACILITY_NAME = 'facility_name',
  AMOUNT = 'amount',
  PROCEDURE_CODE = 'procedure_code',
  COMMENT = 'comment',
}

// Field validation character limits for medical billing summary entry fields
export const FACILITY_NAME_MIN = 1
export const FACILITY_NAME_MAX = 50
export const PROCEDURE_CODE_MAX = 50
export const COMMENT_MAX = 400

// Maximum dollar amount for medical billing summary entry amount field
export const AMOUNT_DOLLARS_MAX = 9999999999.99

// Earliest acceptable service date for medical billing summary entry service date field
export const MIN_SERVICE_DATE = '1950-01-01'

/**
 * Map of field character limits for easy lookup by field key
 */
export const FIELD_CHARACTER_LIMITS: Record<EditableEntryField, number | undefined> = {
  [EditableEntryField.FACILITY_NAME]: FACILITY_NAME_MAX,
  [EditableEntryField.PROCEDURE_CODE]: PROCEDURE_CODE_MAX,
  [EditableEntryField.COMMENT]: COMMENT_MAX,
  [EditableEntryField.SERVICE_DATE]: undefined,
  [EditableEntryField.AMOUNT]: undefined,
}

/**
 * Mask configurations for fields that need input masking
 * refer to https://imask.js.org/guide.html for more information
 */

const AMOUNT_MASK_CONFIG = {
  mask: Number,
  scale: 2,
  thousandsSeparator: '',
  radix: '.',
  mapToRadix: ['.'],
  normalizeZeros: false,
  padFractionalZeros: true,
  min: 0,
}

const SERVICE_DATE_MASK_CONFIG: FactoryOpts = {
  mask: 'm{/}`d{/}`Y', // We intentionally use a pattern mask rather than IMask's Date mask because it's too strict for what we want
  lazy: true,
  autofix: 'pad', // this is required here and on the month and day blocks for inputs like "1/" to mask as "01/"
  blocks: {
    // we're aiming for lenient input parsing rather than strict masking, so the mask does not enforce valid values for the blocks beyond length
    // (the rest will be handled by the validation function)
    m: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2, autofix: 'pad' },
    d: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2, autofix: 'pad' },
    Y: { mask: '0000' },
  },
}

export function getMaskConfig(fieldKey: EditableEntryField): FactoryOpts | null {
  switch (fieldKey) {
    case EditableEntryField.AMOUNT:
      return AMOUNT_MASK_CONFIG
    case EditableEntryField.SERVICE_DATE:
      return SERVICE_DATE_MASK_CONFIG
    default:
      return null
  }
}
